/**
 * metrics — the pure model + formatters for the daemon's `GET /metrics` surface
 * that the Host view and the topbar pill headline read (W4 frontend).
 *
 * The daemon answers `503 { available: false, reason }` before its first
 * resource sample lands, and `200 { available: true, cap, sample, history }`
 * thereafter (see `src/daemon/http.ts` `metricsResponse` and
 * `src/scheduler/resource-sampler.ts` `ResourceSample`). This module mirrors
 * that contract as a discriminated {@link MetricsSnapshot} so a consumer must
 * narrow on `available` before touching the gauges — the pre-first-sample state
 * is a real shape, not a thrown error.
 *
 * Every gauge is nullable: a probe failure degrades that one field to `null`
 * rather than inventing a zero, so the formatters render an honest em dash
 * instead of a misleading `0%`. All exports here are pure — no fetch, no clock,
 * no DOM — so the derivations and labels are unit-tested directly.
 */

/** The em dash a missing/null gauge renders as — never a misleading zero. */
export const EM_DASH = '\u2014'

/** Host OS gauges — the same measurements the admission governor consumes, plus CPU%. */
export interface HostResourceFacts {
  /** Available physical memory in MB. `null` when the probe failed. */
  freeRamMb: number | null
  /** One-minute load average. `null` when the probe failed. */
  loadAverage: number | null
  /** Free disk space in GB on the data volume. `null` when the probe failed. */
  freeDiskGb: number | null
  /** Free disk space as a percentage (0–100). `null` when the probe failed. */
  freeDiskPercent: number | null
  /** Host CPU utilization (0–100) over the last sample interval. `null` on probe failure. */
  cpuPercent: number | null
}

/** Trial-job pressure derived from the durable `trial_jobs` store. */
export interface TrialResourceFacts {
  /** Trials occupying a worker slot right now (`leased` + `running`). */
  active: number
  /** Trials admitted-pending: `queued`. */
  queued: number
  /** Trials awaiting reconciliation: `interrupted` + `retryable`. */
  awaitingReconcile: number
  /** Full per-status breakdown, or `null` when the store read failed. */
  byStatus: Record<string, number> | null
}

/** Image-store pressure: build slots and golden-image churn. */
export interface ImageResourceFacts {
  /** Golden images building right now (occupies a build slot). */
  building: number
  /** Builds queued behind the slot cap. */
  queuedBuilds: number
  /** Ready (cacheable) golden images on the host. */
  ready: number
  /** Images whose last build failed. */
  failed: number
  /** Age of the oldest image row in ms, or `null` when the store is empty. */
  oldestImageAgeMs: number | null
  /** Full per-status breakdown, or `null` when the store read failed. */
  byStatus: Record<string, number> | null
}

/** One immutable resource observation (matches the daemon's `ResourceSample`). */
export interface ResourceSample {
  /** ISO-8601 capture time. */
  ts: string
  host: HostResourceFacts
  trials: TrialResourceFacts
  images: ImageResourceFacts
  /** True when any probe/store read failed this sample. */
  degraded: boolean
  /** Human-readable labels of the reads that failed this sample. */
  failures: string[]
}

/** The static concurrency cap the daemon admits trials against. */
export interface ConcurrencyCap {
  maxConcurrentTrials: number
}

/** `GET /metrics` `200` — the daemon has captured at least one sample. */
export interface MetricsReady {
  available: true
  cap: ConcurrencyCap
  /** The latest sample (equals `history[history.length - 1]`). */
  sample: ResourceSample
  /** A bounded recent window, oldest → newest, for the load/CPU time-series. */
  history: ResourceSample[]
}

/** `GET /metrics` `503` — no sample captured yet (honest pre-first-sample state). */
export interface MetricsPending {
  available: false
  reason: string
}

/** The decoded `/metrics` body — narrow on `available` before reading gauges. */
export type MetricsSnapshot = MetricsReady | MetricsPending

/**
 * Free worker slots = cap − active trials. Can go negative if the host is
 * momentarily over-subscribed (more active than the cap), which is surfaced
 * honestly rather than clamped — an operator wants to see an over-committed box.
 */
export function metricsHeadroom(snapshot: MetricsReady): number {
  return snapshot.cap.maxConcurrentTrials - snapshot.sample.trials.active
}

/** The two headline gauges the topbar pill folds in, or null before a sample lands. */
export interface HostHeadline {
  cpuPercent: number | null
  freeRamMb: number | null
}

/** Pull the pill headline (CPU% + free RAM) from a snapshot, or null when not ready. */
export function metricsHeadline(snapshot: MetricsSnapshot | null): HostHeadline | null {
  if (snapshot === null || !snapshot.available) {
    return null
  }
  return { cpuPercent: snapshot.sample.host.cpuPercent, freeRamMb: snapshot.sample.host.freeRamMb }
}

/** `42%` for a present gauge, an em dash for a null (failed) probe. */
export function formatPercent(value: number | null): string {
  return value === null ? EM_DASH : `${Math.round(value)}%`
}

/** `7.20` (two decimals) for a 1-min load average, an em dash when null. */
export function formatLoad(value: number | null): string {
  return value === null ? EM_DASH : value.toFixed(2)
}

/** Free memory as `1.2 GB` past a gigabyte, `512 MB` below it, an em dash when null. */
export function formatRamMb(freeRamMb: number | null): string {
  if (freeRamMb === null) {
    return EM_DASH
  }
  if (freeRamMb >= 1024) {
    return `${(freeRamMb / 1024).toFixed(1)} GB`
  }
  return `${Math.round(freeRamMb)} MB`
}

/** Free disk as `61 GB (43%)`, degrading each half to an em dash independently. */
export function formatDisk(freeDiskGb: number | null, freeDiskPercent: number | null): string {
  const gb = freeDiskGb === null ? EM_DASH : `${Math.round(freeDiskGb)} GB`
  const pct = freeDiskPercent === null ? EM_DASH : `${Math.round(freeDiskPercent)}%`
  return `${gb} (${pct})`
}

/** The image-build pressure line: `2 building · 1 queued · 5 ready · 1 failed`. */
export function formatImagePressure(images: ImageResourceFacts): string {
  return [
    `${images.building} building`,
    `${images.queuedBuilds} queued`,
    `${images.ready} ready`,
    `${images.failed} failed`,
  ].join(' \u00b7 ')
}
